import {useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {CheckCircle, XCircle, AlertTriangle, Code, KeyRound, FilePen, Power, PowerOff, FlaskConical} from 'lucide-react'
import {useNavigate, useParams, Link} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useOpenRequestGuard} from '@/hooks/use-open-request-guard'
import {useDialog} from '@/hooks/useDialog'
import {Button} from '@/components/ui/button'
import {OpenRequestBanner} from '@/components/shared/OpenRequestBanner'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {OwnersSection, OwnersEntityType} from '@/components/owners/OwnersSection'
import {useAppConfig} from '@/hooks/use-app-config'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {CardSection} from '@/components/shared/CardSection'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {useAuth} from '@/contexts/auth-context'
import {RestrictionRuleRequestType, type RestrictionRuleRequest} from '@/types/restriction-rule-request'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {permissions} from '@/lib/permissions'
import {formatTimestamp} from '@/lib/date-utils'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {PageLayout} from '@/components/shared/PageLayout'
import {IconText} from '@/components/shared/IconText'
import {MutedText} from '@/components/shared/MutedText'
import {EntityLink} from '@/components/shared/EntityLink'
import {DetailPageGrid} from '@/components/shared/DetailPageGrid'
import {DetectionHistoryCard} from '@/components/restriction-rules/DetectionHistoryCard'
import {VersionHistoryCard} from '@/components/restriction-rules/VersionHistoryCard'
import {TestResultsPanel} from '@/components/restriction-rules/TestResultsPanel'
import type {RestrictionRuleTestResult} from '@/types/restriction-rule'
import type {RestrictionRuleRequestCreateRequest} from '@/types/restriction-rule-request'

const PARAM_TYPE = 'type'
const PARAM_RULE_ID = 'ruleId'
const PARAM_MODE = 'mode'

export function RestrictionRuleDetail() {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {id} = useParams<{ id: string }>()
    const {hasPermission} = useAuth()

    const [testResult, setTestResult] = useState<RestrictionRuleTestResult | undefined>(undefined)
    const [testTriggered, setTestTriggered] = useState(false)

    const activateDialog = useDialog()
    const deactivateDialog = useDialog()

    const canCreateRequest = hasPermission(permissions.restrictionRuleRequests.create)
    const canTest = hasPermission(permissions.restrictionRules.test)
    const canUpdate = hasPermission(permissions.restrictionRules.edit)
    const {restrictionRuleResponsibilityTypes} = useAppConfig()

    const {data: rule, isLoading, error} = useQuery({
        queryKey: ['restriction-rule', id],
        queryFn: () => services.restrictionRules.detail(id!),
        enabled: !!id,
    })

    usePageTitle(rule?.name ?? i18n.restrictionRules.detailPageTitle)

    const {data: profiles = []} = useQuery({
        queryKey: ['restriction-rule-profiles', id],
        queryFn: () => services.restrictionRules.profiles(id!),
        enabled: !!id,
    })

    const {openRequest, hasOpenRequest} = useOpenRequestGuard<RestrictionRuleRequest>({
        queryKey: ['restriction-rule-open-request', id],
        queryFn: () => services.restrictionRuleRequests.list({ruleId: id!, status: ['DRAFT', 'PENDING'], size: 1}),
        enabled: !!id,
    })

    const testMutation = useEntityMutation({
        mutationFn: () => services.restrictionRules.testSavedRule(id!),
        invalidate: [],
        onSuccess: (data: RestrictionRuleTestResult) => {
            setTestResult(data)
            setTestTriggered(true)
        },
    })

    const createRequestMutation = useEntityMutation({
        mutationFn: (data: RestrictionRuleRequestCreateRequest) =>
            services.restrictionRuleRequests.create(data),
        invalidate: [['restriction-rule-open-request', id], ['restriction-rule', id]],
        successMessage: i18n.restrictionRuleRequests.requestCreated,
        onSuccess: (request) => {
            activateDialog.close()
            deactivateDialog.close()
            navigate(routes.accessGovernance.restrictionRuleRequests.detail(request.id))
        },
    })

    const handleEdit = () => {
        if (!rule) return
        const url = new URL(routes.accessGovernance.restrictionRuleRequests.create(), window.location.origin)
        url.searchParams.set(PARAM_MODE, 'update')
        url.searchParams.set(PARAM_RULE_ID, rule.id)
        url.searchParams.set(PARAM_TYPE, RestrictionRuleRequestType.UPDATE)
        navigate(url.pathname + url.search)
    }

    const handleActivate = () => {
        if (!rule) return
        createRequestMutation.mutate({
            requestType: RestrictionRuleRequestType.ACTIVATE,
            ruleId: rule.id,
            name: rule.name,
            description: rule.description || undefined,
            conditionExpr: rule.conditionExpr || undefined,
            deniedProfileIds: profiles.map(p => p.accessProfile.id),
            removalGraceDays: rule.removalGraceDays,
            owners: rule.owners.map(o => ({ownerPartyId: o.ownerPartyId, responsibility: o.responsibility})),
        })
    }

    const handleDeactivate = () => {
        if (!rule) return
        createRequestMutation.mutate({
            requestType: RestrictionRuleRequestType.DEACTIVATE,
            ruleId: rule.id,
            name: rule.name,
            description: rule.description || undefined,
            conditionExpr: rule.conditionExpr || undefined,
            deniedProfileIds: profiles.map(p => p.accessProfile.id),
            removalGraceDays: rule.removalGraceDays,
            owners: rule.owners.map(o => ({ownerPartyId: o.ownerPartyId, responsibility: o.responsibility})),
        })
    }

    return (
        <PageLayout
            variant="detail"
            title={rule?.name ?? i18n.restrictionRules.detailPageTitle}
            icon={CONCEPT_ICON.restrictionRule}
            backTo={routes.accessGovernance.restrictionRules.list()}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(rule, 'RESTRICTION_RULE') : undefined)}
            data-testid="restriction-rule-detail-page"
            description={rule ? (
                <InlineRow gap="sm" data-testid="rule-status">
                    {rule.active ? (
                        <Badge variant={BadgeVariant.SUCCESS} className="gap-1" data-testid="badge-active">
                            <CheckCircle className="h-3 w-3"/>
                            {i18n.common.active}
                        </Badge>
                    ) : (
                        <Badge variant={BadgeVariant.SECONDARY} className="gap-1" data-testid="badge-inactive">
                            <XCircle className="h-3 w-3"/>
                            {i18n.common.inactive}
                        </Badge>
                    )}
                    {rule.openViolationCount > 0 && (
                        <Badge variant="destructive" data-testid="badge-violations">
                            <IconText icon={AlertTriangle} size="xs">
                                {i18n.restrictionRules.openViolationsCount({count: rule.openViolationCount})}
                            </IconText>
                        </Badge>
                    )}
                </InlineRow>
            ) : undefined}
            actions={rule ? (
                <InlineRow>
                    {canTest && (
                        <Button
                            data-testid="btn-test-restriction-rule"
                            variant="outline"
                            disabled={testMutation.isPending}
                            onClick={() => testMutation.mutate()}
                        >
                            <IconText icon={FlaskConical}>
                                {testMutation.isPending ? i18n.restrictionRules.testing : i18n.restrictionRules.testRule}
                            </IconText>
                        </Button>
                    )}
                    {canCreateRequest && (
                        <>
                            <Button
                                data-testid="btn-edit"
                                variant="outline"
                                disabled={hasOpenRequest}
                                onClick={handleEdit}
                            >
                                <IconText icon={FilePen}>{i18n.restrictionRules.edit}</IconText>
                            </Button>
                            {rule.active ? (
                                <Button
                                    data-testid="btn-deactivate"
                                    variant="outline"
                                    disabled={hasOpenRequest}
                                    onClick={deactivateDialog.open}
                                >
                                    <IconText icon={PowerOff}>{i18n.restrictionRules.deactivateRule}</IconText>
                                </Button>
                            ) : (
                                <Button
                                    data-testid="btn-activate"
                                    variant="outline"
                                    disabled={hasOpenRequest}
                                    onClick={activateDialog.open}
                                >
                                    <IconText icon={Power}>{i18n.restrictionRules.activateRule}</IconText>
                                </Button>
                            )}
                        </>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {rule && <>
            {hasOpenRequest && openRequest && (
                <OpenRequestBanner
                    message={i18n.restrictionRuleRequests.openRequestExists}
                    linkTo={routes.accessGovernance.restrictionRuleRequests.detail(openRequest.id)}
                    requestName={openRequest.name}
                    testId="open-request-banner"
                    linkTestId="link-open-request"
                />
            )}

            <DetailPageGrid
                main={<>
                    <DetailsCard
                        data-testid="card-rule-details"
                        title={i18n.restrictionRules.ruleDetails}
                        fields={[
                            ...(rule.description ? [{label: i18n.common.description, value: rule.description, fullWidth: true as const}] : []),
                            {
                                label: i18n.restrictionRules.removalGraceDays,
                                /* eslint-disable-next-line i18next/no-literal-string */
                                value: <span data-testid="rule-grace-days">{rule.removalGraceDays} days</span>,
                            },
                        ]}
                    />

                    <OwnersSection
                        entityType={OwnersEntityType.restrictionRules}
                        entityId={id!}
                        canEdit={canUpdate}
                        responsibilityOptions={restrictionRuleResponsibilityTypes}
                    />

                    <CardSection
                        icon={Code}
                        title={String(i18n.restrictionRules.conditionExpression)}
                        description={String(i18n.restrictionRules.conditionExpressionDesc)}
                        data-testid="section-condition-expression"
                        data-help-prefix="restrictionRuleDetail.conditionExpression"
                    >
                        {rule.conditionExpr ? (
                            <CodeBlock data-testid="rule-condition-expr">
                                {rule.conditionExpr}
                            </CodeBlock>
                        ) : (
                            <MutedText block>{i18n.restrictionRules.noConditionExpression}</MutedText>
                        )}
                    </CardSection>

                    {testTriggered && (
                        <TestResultsPanel
                            result={testResult}
                            isLoading={testMutation.isPending}
                            isError={testMutation.isError}
                        />
                    )}

                    <CardSection
                        icon={KeyRound}
                        title={String(i18n.restrictionRules.deniedProfiles)}
                        description={String(i18n.restrictionRules.deniedProfilesCount({count: profiles.length}))}
                        data-testid="section-denied-profiles"
                        data-help-prefix="restrictionRuleDetail.deniedProfiles"
                    >
                        {profiles.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-profile-code">{i18n.restrictionRules.profileCode}</TableHead>
                                        <TableHead data-testid="col-profile-name">{i18n.restrictionRules.profileName}</TableHead>
                                        <TableHead data-testid="col-resource">{i18n.restrictionRules.resource}</TableHead>
                                        <TableHead data-testid="col-system">{i18n.restrictionRules.system}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {profiles.map((profile) => (
                                        <TableRow key={profile.id}>
                                            <TableCell>
                                                <Badge variant="outline">{profile.accessProfile.code}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <EntityLink value={profile.accessProfile} testId={`link-access-profile-${profile.accessProfile.id}`}/>
                                            </TableCell>
                                            <TableCell>
                                                <EntityLink value={profile.resource}/>
                                            </TableCell>
                                            <TableCell>
                                                <EntityLink value={profile.targetSystem}/>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <MutedText block centered className="py-4">
                                {i18n.restrictionRules.noProfiles}
                            </MutedText>
                        )}
                    </CardSection>
                </>}
                sidebar={
                    <Stack gap="lg">
                        <DetailsCard
                            title={i18n.restrictionRules.ruleInformation}
                            data-testid="rule-info-card"
                            data-help-prefix="restrictionRuleDetail.ruleInformation"
                            fields={[
                                {label: i18n.restrictionRules.deniedProfiles, value: rule.profileCount},
                                {
                                    label: i18n.restrictionRules.viewViolations,
                                    value: (
                                        <Link to={routes.accessGovernance.restrictionRules.violationsForRule(rule.id)} data-testid="link-violations">
                                            {rule.openViolationCount > 0 ? (
                                                <Badge variant="destructive" data-testid="badge-open-violations">
                                                    {rule.openViolationCount}
                                                </Badge>
                                            ) : (
                                                <Badge variant={BadgeVariant.SECONDARY} data-testid="badge-open-violations">
                                                    0
                                                </Badge>
                                            )}
                                        </Link>
                                    ),
                                },
                                {label: i18n.common.createdAt, value: formatTimestamp(rule.createdAt)},
                                {label: i18n.common.lastUpdated, value: formatTimestamp(rule.updatedAt)},
                            ]}
                        />
                        <VersionHistoryCard ruleId={rule.id}/>
                        <DetectionHistoryCard ruleId={rule.id}/>
                    </Stack>
                }
            />

            <Dialog open={activateDialog.isOpen} onOpenChange={activateDialog.bind.onOpenChange}>
                <DialogContent data-testid="dialog-activate-rule">
                    <DialogHeader>
                        <DialogTitle>{i18n.restrictionRules.activateRule}</DialogTitle>
                        <DialogDescription>
                            {i18n.restrictionRules.activateRuleDesc({name: rule.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="activate"
                        onCancel={activateDialog.close}
                        saving={createRequestMutation.isPending}
                        saveLabel={i18n.restrictionRules.createActivateRequest}
                        savingLabel={i18n.common.creating}
                        saveType="button"
                        onSave={handleActivate}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={deactivateDialog.isOpen} onOpenChange={deactivateDialog.bind.onOpenChange}>
                <DialogContent data-testid="dialog-deactivate-rule">
                    <DialogHeader>
                        <DialogTitle>{i18n.restrictionRules.deactivateRule}</DialogTitle>
                        <DialogDescription>
                            {i18n.restrictionRules.deactivateRuleDesc({name: rule.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="deactivate"
                        onCancel={deactivateDialog.close}
                        saving={createRequestMutation.isPending}
                        saveLabel={i18n.restrictionRules.createDeactivateRequest}
                        savingLabel={i18n.common.creating}
                        destructive
                        saveType="button"
                        onSave={handleDeactivate}
                    />
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
